import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useData } from 'vike-react/useData'

import { ImgSanity } from '@slite/components'

import { WebsiteCard } from '../../components/Card/Card'
import { Section } from '../../components/Section'
import { SectionCurve } from '../../components/SectionCurve'
import { getContentPrinciples } from '../../components/SectionFAQ/content-principles'
import { SingleFAQ } from '../../components/SectionFAQ/SingleFAQ'
import { SlitePage } from '../../components/SlitePage'
import { WebsiteText } from '../../components/WebsiteText'
import { useRoutes } from '../../routes'
import type { Data } from './+data'
import { getAboutBlogCards } from './blog-cards'
import { getContentFAQ } from './content-faq'
import { AboutSectionTeam } from './SectionTeam'
import { SectionTitle } from './SectionTitle'

const SCROLL_MARGIN_TOP = 120

export default function AboutPage() {
  const { teamPeople } = useData<Data>()
  const intl = useIntl()
  const routes = useRoutes()

  const title = intl.formatMessage({
    id: 'about.page.title',
    defaultMessage: 'About Slite | A guide to Remote',
  })

  const description = intl.formatMessage({
    id: 'about.page.description',
    defaultMessage:
      "Slite is a remote company that creates software for other remote companies to thrive in remote. We believe in remote work because it's more than just an alternative to the office. It's a life upgrade.",
  })

  const FAQ = getContentFAQ(intl)
  const principles = getContentPrinciples(intl)
  const blogCards = getAboutBlogCards(intl, routes)

  const onClickOnAnchor = (
    e: React.MouseEvent<HTMLAnchorElement>,
    id: string
  ) => {
    e.preventDefault()
    document.getElementById(id)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
  }

  return (
    <SlitePage
      canonicalUrl={routes.about()}
      title={title}
      description={description}
      background="moon-dust"
      footerBackground="shade-100"
      ogImage="https://cdn.sanity.io/images/dnsgqii5/production/b69a940005a7bd5d1691b5e4431ebb4516e0253b-1200x630.jpg"
    >
      <div
        className="background-moon-dust"
        style={{
          minHeight: '50vh',
          backgroundImage: `
            url(https://cdn.sanity.io/images/dnsgqii5/production/ce4fdd80f8899b8259a3755a41eec7c6a0fa8ebf-569x595.svg),
            url(https://cdn.sanity.io/images/dnsgqii5/production/845b9f74552e7eda3e48a43c751f07ebd7e3b123-629x554.svg)
          `,
          backgroundRepeat: 'no-repeat, no-repeat',
          backgroundSize: 'auto 60%, auto 60%',
          backgroundPosition: 'left bottom, right bottom',
        }}
      >
        <Section>
          <WebsiteText
            variant="display"
            className="text-align-center margin-top-40"
          >
            <FormattedMessage
              defaultMessage="In good company."
              id="about.page.intro"
            />
          </WebsiteText>
        </Section>
      </div>

      <SectionCurve
        from="transparent"
        to="shade-100"
        shape="round-flat"
        style={{ transform: 'translateY(-100%)' }}
      />

      <Section containerTopPadding={0} style={{ marginTop: -60 }}>
        <div className="grid-12">
          <div className="grid-span-3 display-none-medium display-none-small">
            <div style={{ position: 'sticky', top: 100 }}>
              <a
                href="#about-story"
                onClick={(e) => onClickOnAnchor(e, 'about-story')}
                className="block pointer font-family-title font-weight-medium shade-20 padding-vertical-16"
              >
                <FormattedMessage
                  defaultMessage="Story"
                  id="about.page.intro.content1"
                />
              </a>
              <a
                href="#about-principles"
                onClick={(e) => onClickOnAnchor(e, 'about-principles')}
                className="block pointer font-family-title font-weight-medium shade-20 padding-vertical-16"
              >
                <FormattedMessage
                  defaultMessage="Principles"
                  id="about.page.intro.content2"
                />
              </a>
              <a
                href="#about-sustainability"
                onClick={(e) => onClickOnAnchor(e, 'about-sustainability')}
                className="block pointer font-family-title font-weight-medium shade-20 padding-vertical-16"
              >
                <FormattedMessage
                  defaultMessage="Sustainability"
                  id="about.page.intro.content3"
                />
              </a>
              <a
                href="#about-supporters"
                onClick={(e) => onClickOnAnchor(e, 'about-supporters')}
                className="block pointer font-family-title font-weight-medium shade-20 padding-vertical-16"
              >
                <FormattedMessage
                  defaultMessage="Supporters"
                  id="about.page.intro.content4"
                />
              </a>
            </div>
          </div>
          <div className="grid-span-9-large grid-span-12">
            {/* Story */}
            <div
              className="margin-bottom-60"
              id="about-story"
              style={{ scrollMarginTop: SCROLL_MARGIN_TOP }}
            >
              <SectionTitle>
                <FormattedMessage
                  defaultMessage="Story"
                  id="about.page.story"
                />
              </SectionTitle>
              <WebsiteText variant="body-lg" className="margin-bottom-12">
                <FormattedMessage
                  defaultMessage="We founded Slite in 2017 to build the most intuitive knowledge base for teams."
                  id="about.page.story.content1"
                />
              </WebsiteText>
              <WebsiteText variant="body-lg" className="margin-bottom-12">
                <FormattedMessage
                  defaultMessage="Since then, it has constantly been praised for its ease of use, attention to detail, and stellar customer experience."
                  id="about.page.story.content2"
                />
              </WebsiteText>
              <WebsiteText variant="body-lg" className="margin-bottom-12">
                <FormattedMessage
                  defaultMessage="In 2025, we went multi product and built its cousin, Super, for the AI era. Super is teams' ultimate AI search on all their tools, and a way for them to accelerate all their workflows with agents able to tap on their collective knowledge."
                  id="about.page.story.content3"
                />
              </WebsiteText>
              <WebsiteText variant="body-lg" className="margin-bottom-32">
                <FormattedMessage
                  defaultMessage="Since inception we've been working in remote and promoting good ways to do so."
                  id="about.page.story.content4"
                />
              </WebsiteText>

              <ImgSanity
                style={{ marginLeft: -8, marginRight: -8 }}
                src="https://cdn.sanity.io/images/dnsgqii5/production/6179a7e90b52f4a5c1d0449ac5ed317c701a5ce7-4964x3748.png"
                alt={intl.formatMessage({
                  id: 'about.page.story.tiles.imageAlt',
                  defaultMessage: 'Slite history tiles',
                })}
              />
              <p className="font-family-title shade-20 font-size-7 text-align-right padding-right-16">
                <FormattedMessage
                  defaultMessage="*Employees likely to recommend Slite as a place to work in 2022."
                  id="about.page.story.tiles.caption"
                />
              </p>
            </div>

            {/* Principles */}
            <div
              className="margin-bottom-60 padding-bottom-40"
              id="about-principles"
              style={{ scrollMarginTop: SCROLL_MARGIN_TOP }}
            >
              <SectionTitle>
                <FormattedMessage
                  defaultMessage="Principles"
                  id="about.page.principles"
                />
              </SectionTitle>
              <div className="grid-12 grid-column-gap-40 grid-row-gap-40 margin-top-40 grid-column-gap-0-small">
                {principles.map((section) => (
                  <div
                    className="grid-span-6 grid-span-12-small"
                    key={section.title}
                  >
                    <ImgSanity
                      className="margin-bottom-20"
                      style={{ height: 180 }}
                      src={section.image.src}
                      alt={section.image.alt}
                    />
                    <h3 className="font-family-title font-size-4 font-weight-medium margin-bottom-8">
                      {section.title}
                    </h3>
                    <WebsiteText
                      variant="body"
                      className="font-weight-semibold"
                    >
                      {section.content}
                    </WebsiteText>
                  </div>
                ))}
              </div>
            </div>

            {/* Sustainability */}
            <div
              id="about-sustainability"
              className="margin-bottom-60"
              style={{ scrollMarginTop: SCROLL_MARGIN_TOP }}
            >
              <div className="flexbox flexbox-align-center flexbox-space-between margin-bottom-32 flexbox-column-reverse-small flexbox-row-gap-32-small">
                <h2 className="margin-bottom-8 line-height-large font-size-2 font-family-title font-weight-medium">
                  <FormattedMessage
                    defaultMessage="Our commitment {br} to sustainability"
                    id="about.page.sustainability"
                    values={{ br: <br /> }}
                  />
                </h2>
                <ImgSanity
                  style={{ width: 130 }}
                  src="https://cdn.sanity.io/images/dnsgqii5/production/730df4ec90acbe1ca13c048f100d82a54368edae-639x623.png"
                  alt={intl.formatMessage({
                    id: 'about.page.sustainability.imageAlt',
                    defaultMessage: 'Sustainability illustration',
                  })}
                />
              </div>
              <p className="font-family-title font-size-4 margin-bottom-20">
                <FormattedMessage
                  defaultMessage="“We build Slite for the long run, always looking at the impact we have on our team and the people who use Slite, as well as the broader world.” — Christophe Pasquier, Slite CEO."
                  id="about.page.sustainability.quote"
                />
              </p>
              <WebsiteText variant="body-lg" className="margin-bottom-40">
                <FormattedMessage
                  defaultMessage="Our aim is to improve the way we think about work now, and for future generations to come."
                  id="about.page.sustainability.content1"
                />
              </WebsiteText>

              <div className="flexbox flexbox-column margin-bottom-60">
                {FAQ.map((section, index) => (
                  <div key={`${index}-${section.question.toString()}`}>
                    {index !== 0 && (
                      <div
                        className="width-full background-shade-30 margin-bottom-24 margin-top-16"
                        style={{ height: 1 }}
                      />
                    )}
                    <SingleFAQ section={section} defaultIsOpen={index === 0} />
                  </div>
                ))}
              </div>

              <div>
                <h6 className="font-family-title font-weight-medium font-size-6 margin-bottom-16">
                  <FormattedMessage
                    defaultMessage="Read more on our journey"
                    id="about.page.sustainability.readMore"
                  />
                </h6>
                <div className="grid-12 grid-gap-32">
                  {blogCards.map((card) => (
                    <WebsiteCard
                      href={card.href}
                      key={card.title}
                      className="grid-span-6 padding-horizontal-4 padding-vertical-4 grid-span-12-small"
                    >
                      <ImgSanity
                        className="display-block margin-bottom-8 radius-semilarger"
                        src={card.imageSrc}
                        alt={card.title}
                      />
                      <h5
                        className="font-family-title font-weight-medium margin-bottom-4 font-size-5 shade-05"
                        style={{ fontSize: '1rem' }}
                      >
                        {card.title}
                      </h5>
                      <WebsiteText variant="body" className="shade-10">
                        {card.description}
                      </WebsiteText>
                    </WebsiteCard>
                  ))}
                </div>
              </div>
            </div>

            <AboutSectionTeam
              teamPeople={teamPeople}
              className="margin-bottom-60"
            />

            {/* Supporters / Backed by */}
            <div
              id="about-supporters"
              style={{ scrollMarginTop: SCROLL_MARGIN_TOP }}
            >
              <SectionTitle>
                <FormattedMessage
                  defaultMessage="Supporters"
                  id="about.page.supporters"
                />
              </SectionTitle>
              <WebsiteText variant="title" className="margin-bottom-40">
                <FormattedMessage
                  defaultMessage="Backed by"
                  id="about.page.backedBy.content"
                />
              </WebsiteText>

              <div
                className="background-shade-00 padding-horizontal-40 radius-larger flexbox flexbox-centered flexbox-column-small flexbox-row-gap-40"
                style={{ columnGap: 64, paddingTop: 56, paddingBottom: 56 }}
              >
                <ImgSanity
                  src="https://cdn.sanity.io/images/dnsgqii5/production/d02329291f13d3cca6dfdcb683d3f310b17a4cb0-100x40.svg"
                  alt={intl.formatMessage({
                    id: 'about.page.backedBy.imageAlt1',
                    defaultMessage: 'Backed by Spark Capital',
                  })}
                />
                <ImgSanity
                  src="https://cdn.sanity.io/images/dnsgqii5/production/fbd85c637e03dd416cffd967cb4aeb73243203f5-140x40.svg"
                  alt={intl.formatMessage({
                    id: 'about.page.backedBy.imageAlt2',
                    defaultMessage: 'Backed by eFounders',
                  })}
                />
                <ImgSanity
                  src="https://cdn.sanity.io/images/dnsgqii5/production/f036f27d33bb03ee5051bb16a549868e158b5db8-140x38.svg"
                  alt={intl.formatMessage({
                    id: 'about.page.backedBy.imageAlt3',
                    defaultMessage: 'Backed by Index Ventures',
                  })}
                />
                <ImgSanity
                  src="https://cdn.sanity.io/images/dnsgqii5/production/a0cf8559a3df4eeaaaf74bac8bb92e674de4af46-140x29.svg"
                  alt={intl.formatMessage({
                    id: 'about.page.backedBy.imageAlt4',
                    defaultMessage: 'Backed by Y Combinator',
                  })}
                />
              </div>
            </div>
          </div>
        </div>
      </Section>
    </SlitePage>
  )
}
